import { useRectNodeConnections } from "./RectNodeConnectionsContext";
import { useState } from "react";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import { clientToSvgUserSpace } from "../../utils/svgViewBoxPointer";

export function RectNodeConnectionsViewer() {
    const {
        nodes,
        connections,
        nodeColor,
        nodeFillEnabled,
        strokeColor,
        connectionOpacity,
        viewBoxAttr,
        addNode,
        brushSize,
        getNodeRectHalfExtents,
        undo,
        redo,
    } = useRectNodeConnections();

    useUndoRedoKeyboard({ undo, redo });

    const [brushHover, setBrushHover] = useState<{
        x: number;
        y: number;
    } | null>(null);

    const handleSvgClick = (
        event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    ) => {
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBoxAttr,
        );
        if (!pt) return;
        addNode(pt.x, pt.y);
    };

    const handleSvgPointerMove = (
        event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    ) => {
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBoxAttr,
        );
        setBrushHover(pt);
    };

    const handleSvgPointerLeave = () => {
        setBrushHover(null);
    };

    return (
        <div className="panel-viewer rect-node-connections-viewer">
            <div className="path-tool-preview">
                <div className="svg-scroll-wrapper">
                    <svg
                        viewBox={viewBoxAttr}
                        xmlns="http://www.w3.org/2000/svg"
                        className="path-tool-svg"
                        onClick={handleSvgClick}
                        onPointerMove={handleSvgPointerMove}
                        onPointerLeave={handleSvgPointerLeave}
                        style={{ cursor: "crosshair" }}
                    >
                        <g className="connections">
                            {connections.map((conn, idx) => {
                                const fromNode = nodes.find(
                                    (n) => n.id === conn.from,
                                );
                                const toNode = nodes.find(
                                    (n) => n.id === conn.to,
                                );
                                if (!fromNode || !toNode) return null;

                                return (
                                    <line
                                        key={`${conn.from}-${conn.to}-${idx}`}
                                        x1={fromNode.x}
                                        y1={fromNode.y}
                                        x2={toNode.x}
                                        y2={toNode.y}
                                        stroke={strokeColor}
                                        strokeWidth="2"
                                        opacity={connectionOpacity}
                                    />
                                );
                            })}
                        </g>

                        <g className="nodes">
                            {nodes.map((node) => {
                                const { hw, hh } = getNodeRectHalfExtents(
                                    node.id,
                                );
                                const x = node.x - hw;
                                const y = node.y - hh;
                                const rw = 2 * hw;
                                const rh = 2 * hh;
                                return (
                                    <g key={node.id}>
                                        <rect
                                            x={x}
                                            y={y}
                                            width={rw}
                                            height={rh}
                                            fill={
                                                nodeFillEnabled
                                                    ? nodeColor
                                                    : "none"
                                            }
                                            stroke={strokeColor}
                                            strokeWidth="2"
                                        />
                                        <path
                                            d={`M ${node.x - hw} ${node.y - hh} L ${node.x + hw} ${node.y + hh} M ${node.x + hw} ${node.y - hh} L ${node.x - hw} ${node.y + hh}`}
                                            fill="none"
                                            stroke={strokeColor}
                                            strokeWidth="2"
                                        />
                                    </g>
                                );
                            })}
                        </g>
                        {brushHover && brushSize > 0 && (
                            <circle
                                className="svg-brush-hover-preview"
                                cx={brushHover.x}
                                cy={brushHover.y}
                                r={brushSize}
                                stroke={strokeColor}
                            />
                        )}
                    </svg>
                </div>
            </div>
        </div>
    );
}
